import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { completeOnboarding, useAction } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { ChevronUp, ArrowRight, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import useTranslation from "../hooks/useTranslation";
import { useOnboardingStatus } from "../hooks/useOnboardingStatus";
import {
  trackOnboardingCompleted,
  trackOnboardingCtaClicked,
  trackOnboardingShown,
  trackOnboardingStepCompleted,
} from "../utils/posthog";
import confetti from "canvas-confetti";

type ModalState = "full" | "pill" | "hidden";

function fireConfetti(final = false) {
  if (final) {
    const end = Date.now() + 3000;
    const frame = () => {
      confetti({
        particleCount: 6,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        disableForReducedMotion: true,
      });
      confetti({
        particleCount: 6,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        disableForReducedMotion: true,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  } else {
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      disableForReducedMotion: true,
    });
  }
}

export default function OnboardingModal() {
  const { data: user } = useAuth();
  const t = useTranslation();
  const navigate = useNavigate();
  const { steps, completedCount, allCompleted, isLoading } = useOnboardingStatus(
    user?.id
  );
  const completeOnboardingAction = useAction(completeOnboarding);

  const [modalState, setModalState] = useState<ModalState>("hidden");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [animDir, setAnimDir] = useState<"enter" | "exit">("enter");

  const prevCompletedRef = useRef<Set<string>>(new Set());
  const hasInitialized = useRef(false);
  const hasCompletedOnboarding = useRef(false);

  // Initialize: show modal if onboarding not done
  useEffect(() => {
    if (!user || isLoading || hasInitialized.current) return;
    hasInitialized.current = true;

    const alreadyDone = Boolean((user as any).onboardingCompletedAt);
    if (alreadyDone) {
      setModalState("hidden");
      return;
    }

    // Seed the initial completed set without triggering celebrations
    const initial = new Set(steps.filter((s) => s.completed).map((s) => s.id));
    prevCompletedRef.current = initial;

    // Find first incomplete step
    const firstIncomplete = steps.findIndex((s) => !s.completed);
    setCurrentStepIndex(firstIncomplete === -1 ? 0 : firstIncomplete);
    setModalState("full");
    trackOnboardingShown("advanced", steps[firstIncomplete === -1 ? 0 : firstIncomplete]?.id ?? "account");
  }, [user, isLoading, steps]);

  // Detect newly completed steps
  useEffect(() => {
    if (!hasInitialized.current || modalState === "hidden") return;

    const prev = prevCompletedRef.current;
    const newlyCompleted = steps.filter(
      (s) => s.completed && !prev.has(s.id)
    );

    if (newlyCompleted.length === 0) return;

    newlyCompleted.forEach((s) => {
      prev.add(s.id);
      trackOnboardingStepCompleted("advanced", s.id);
    });

    if (allCompleted && !hasCompletedOnboarding.current) {
      hasCompletedOnboarding.current = true;
      setModalState("full");
      setAllDone(true);
      fireConfetti(true);
      return;
    }

    // Celebrate the completed step then advance
    setModalState("full");
    setCelebrating(true);
    fireConfetti(false);

    setTimeout(() => {
      setCelebrating(false);
      setAnimDir("exit");
      setTimeout(() => {
        const nextIndex = steps.findIndex((s) => !s.completed);
        setCurrentStepIndex(nextIndex === -1 ? steps.length - 1 : nextIndex);
        setAnimDir("enter");
      }, 300);
    }, 1800);
  }, [steps, allCompleted, modalState]);

  const handleCta = useCallback(() => {
    const step = steps[currentStepIndex];
    if (!step) return;
    trackOnboardingCtaClicked("advanced", step.id);
    setModalState("pill");
    navigate(step.href);
  }, [steps, currentStepIndex, navigate]);

  const handleFinish = useCallback(async () => {
    await completeOnboardingAction(undefined as any);
    trackOnboardingCompleted("advanced");
    setModalState("hidden");
  }, [completeOnboardingAction]);

  if (modalState === "hidden" || !user || isLoading) return null;

  const progressPercent = (completedCount / steps.length) * 100;
  const stepLabel = t.onboarding.stepOf
    .replace("{current}", String(completedCount + 1 > steps.length ? steps.length : completedCount + 1))
    .replace("{total}", String(steps.length));

  if (modalState === "pill") {
    const step = steps[currentStepIndex];
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <button
          onClick={() => setModalState("full")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border shadow-lg hover:shadow-xl transition-all animate-pulse-subtle text-sm font-medium text-foreground"
          title={t.onboarding.pillExpand}
        >
          <span className="text-base">{step?.emoji}</span>
          <span className="max-w-[180px] truncate">{t.onboarding[step?.titleKey ?? "step1Title"]}</span>
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </button>
      </div>
    );
  }

  // Full modal
  const step = steps[currentStepIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-3 pt-6 px-6">
          {steps.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center rounded-full transition-all duration-300 ${
                  s.completed
                    ? "w-7 h-7 bg-primary text-white"
                    : i === currentStepIndex
                    ? "w-7 h-7 border-2 border-primary text-primary"
                    : "w-7 h-7 border-2 border-border text-muted-foreground"
                }`}
              >
                {s.completed ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <span className="text-xs font-bold">{i + 1}</span>
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 w-8 transition-all duration-500 ${
                    s.completed ? "bg-primary" : "bg-border"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div
          className={`px-8 py-6 text-center transition-all duration-300 ${
            animDir === "enter"
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-8"
          }`}
        >
          {allDone ? (
            <>
              <div className="text-6xl mb-4 animate-bounce">🚀</div>
              <h2
                className="text-xl font-bold text-foreground mb-6"
                style={{ fontFamily: "'Red Hat Display', sans-serif" }}
              >
                {t.onboarding.finalTitle}
              </h2>
              <Button
                onClick={handleFinish}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                {t.onboarding.finalCta}
              </Button>
            </>
          ) : celebrating ? (
            <>
              <div className="text-6xl mb-4 animate-bounce">{step?.emoji}</div>
              <p className="text-lg font-bold text-primary">
                {t.onboarding[step?.celebrationKey ?? "step1Celebration"]}
              </p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">{step?.emoji}</div>
              <h2
                className="text-xl font-bold text-foreground mb-2"
                style={{ fontFamily: "'Red Hat Display', sans-serif" }}
              >
                {t.onboarding[step?.titleKey ?? "step1Title"]}
              </h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                {t.onboarding[step?.descriptionKey ?? "step1Description"]}
              </p>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                  <span>{stepLabel}</span>
                  <span>{Math.round(progressPercent)}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>

              <Button
                onClick={handleCta}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold flex items-center justify-center gap-2"
              >
                {t.onboarding[step?.ctaKey ?? "step1Cta"]}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {/* Bottom progress accent */}
        <div
          className="h-1 bg-primary transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}
